
import type { User } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { UserRoleManagementMessages } from '../../models'
import { DomainArrayUtils } from '@empathyco/playboard-core-domain'
import { computed, defineComponent, nextTick, ref } from 'vue'
import { compileMessage } from '../../utils/messages.utils'
import { UiModal } from '../keen-ui'
import { EpbButton } from '../shared/epb-button'
import RoleAssignmentInstanceCondition from './role-assignment-instance-condition.component.vue'
import RolePill from './role-pill.component.vue'

export default defineComponent({
  name: 'UserDetailsModal',
  components: {
    EpbButton,
    RoleAssignmentInstanceCondition,
    RolePill,
    UiModal,
  },
  props: {
    user: {
      type: Object as PropType<User>,
      default: undefined,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    messages: {
      type: Object as PropType<UserRoleManagementMessages['details']>,
      required: true,
    },
  },
  emits: ['edit'],
  setup(props) {
    const baseClass = 'user-details-modal'

    const modal = ref()

    const title = computed(() =>
      compileMessage(props.messages.title, {
        user: props.user.fullName,
        instancesCount: props.user.instances?.length.toString(),
      }).toUpperCase(),
    )

    const groupedRoles = computed(() => {
      return props.user.roles.reduce(
        (groups, { id, name, instances }) => {
          const roleInfo = { id, name }

          if (instances) {
            const group = groups.find(i =>
              DomainArrayUtils.containSameElements(instances, i.instances ?? []),
            )
            group ? group.roles.push(roleInfo) : groups.push({ roles: [roleInfo], instances })
          } else {
            groups.push({ roles: [roleInfo] })
          }

          return groups
        },
        [] as { roles: { id: number | undefined; name: string }[]; instances?: string[] }[],
      )
    })

    function open(): void {
      nextTick(() => modal.value.open())
    }

    function close(): void {
      modal.value.close()
    }

    return {
      baseClass,
      modal,
      title,
      groupedRoles,
      open,
      close,
    }
  },
})
